import LanguageIcon from '@mui/icons-material/Language';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography, styled } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from '../../i18n';
import { saveCurrentLang } from './Language.helpers';

const LangMenuItem = styled(MenuItem)(() => ({
  flexDirection: 'row-reverse',
  justifyContent: 'space-between',
  '&': { margin: '8px', padding: '0 16px', height: '32px' },
  '& > .MuiListItemIcon-root': {
    paddingLeft: '18px',
  },
}));

type LanguageProps = {
  resourceName: string;
};

export const Language: FC<LanguageProps> = ({ resourceName }) => {
  const { t } = useTranslation();
  const currentLang = i18n.language;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const languages = useMemo(() => {
    return Object.keys(i18n.options.resources ?? {}).filter(lang => !!i18n.options?.resources?.[lang][resourceName]);
  }, [resourceName]);

  // do not show selection when only one language is available
  if (languages.length <= 1) return null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (lang: string) => {
    i18n.changeLanguage(lang);
    saveCurrentLang(lang, resourceName);
    handleClose();
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuItems = languages.map(lang => (
    <LangMenuItem key={lang} dense disableRipple selected={currentLang === lang} onClick={() => handleSelect(lang)}>
      <ListItemIcon>
        <Typography variant="p12Medium" textTransform="uppercase">
          {lang}
        </Typography>
      </ListItemIcon>
      <ListItemText primaryTypographyProps={{ variant: 'p12' }}>{t(`language.${lang}`)}</ListItemText>
    </LangMenuItem>
  ));

  return (
    <>
      <Tooltip title={t(`language.${currentLang}`)}>
        <Button size="small" onClick={handleClick} startIcon={<LanguageIcon />}>
          <Typography variant="p12Medium" textTransform="uppercase">
            {currentLang}
          </Typography>
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: { sx: { '&&&': { borderRadius: '4px', minWidth: 'auto', marginTop: 0 } } },
        }}
      >
        {menuItems}
      </Menu>
    </>
  );
};
